import { useId, useState, type FormEvent } from 'react';
import { useAuth } from './AuthProvider';
import { apiCompletePasswordSetup } from './authApi';
import { LiftXLogo } from '../../ui/components/LiftXLogo';
import styles from './ForcedPasswordSetupScreen.module.css';

// Mirrors functions/api/_lib/http.ts's MIN_PASSWORD_LENGTH — kept in sync by
// hand, since the frontend and functions/ are separate TypeScript projects
// with no shared import path between them (same as LoginScreen's own
// convention of not importing backend constants).
const MIN_PASSWORD_LENGTH = 8;

/**
 * The one-time forced transition off the provisional bootstrap credential
 * (Master Phase 11 §6). Rendered by `AuthGate` in place of the normal app —
 * never reachable via a route, and never shown again once it succeeds, since
 * `must_change_password` only ever goes `1 → 0`.
 *
 * No "current password" field: having a valid session (via the provisional
 * credential) is itself the proof of identity for this one specific,
 * narrow transition. The value typed here is sent in exactly one request —
 * `POST /api/auth/complete-password-setup` — and is never stored, cached,
 * or logged locally, mirroring `LoginScreen`'s own guarantee about the
 * ordinary login password.
 */
export function ForcedPasswordSetupScreen(): React.JSX.Element {
  const { revalidate } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newId = useId();
  const confirmId = useId();
  const errorId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${String(MIN_PASSWORD_LENGTH)} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const outcome = await apiCompletePasswordSetup(newPassword);
      if (outcome.outcome === 'success') {
        // Releases the gate: a fresh /me now reports mustChangePassword=false.
        await revalidate();
        return;
      }
      if (outcome.outcome === 'network-error') {
        setError('Can’t reach the server. Check your connection and try again.');
      } else {
        setError('Could not set your password. Please try again.');
      }
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.logoWrap}>
          <LiftXLogo height={44} />
        </div>

        <h1 className={styles.title}>Create Your Administrator Password</h1>
        <p className={styles.subtitle}>
          You signed in with a temporary credential. Choose a permanent password to continue — the
          temporary one stops working the moment you do.
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={newId}>
            New password
          </label>
          <input
            id={newId}
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={submitting}
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={confirmId}>
            Confirm password
          </label>
          <input
            id={confirmId}
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={submitting}
            required
            aria-invalid={error !== null}
            aria-describedby={error !== null ? errorId : undefined}
          />
        </div>

        {error !== null && (
          <p id={errorId} role="alert" className={styles.error}>
            {error}
          </p>
        )}

        <button type="submit" className={styles.submit} disabled={submitting}>
          {submitting ? 'Setting password…' : 'Set Administrator Password'}
        </button>
      </form>
    </div>
  );
}
